import VendorDatePicker from 'react-datepicker'
import { forwardRef, MouseEventHandler, ReactElement, useEffect, useState } from 'react'
import { XIcon } from '@heroicons/react/solid'
import { Input } from './Input'
import { useWindowWidth } from 'hooks/UseWindowWidth'
import { shift } from '@floating-ui/react-dom'
import { useTranslation } from 'react-i18next'
import { isEqual } from 'lodash'

export type DateRangeValueFormat = [fromDate: Date | null, toDate: Date | null]

interface DateRangePickerProps {
  value?: DateRangeValueFormat
  onChange: (range: DateRangeValueFormat) => void
  minDate?: Date
  clearButton?: boolean
  valueComponent?: ReactElement
}

export const DateRangePicker = forwardRef<HTMLInputElement, DateRangePickerProps>(
  ({ value, onChange, minDate, clearButton, valueComponent }, ref) => {
    const [selectedRange, setSelectedRange] = useState<DateRangeValueFormat>(value ?? [null, null])

    useEffect(() => {
      setSelectedRange((previous) => {
        if (value && !isEqual(value, previous)) {
          return value
        }

        if (!value) {
          return [null, null]
        }

        return previous
      })
    }, [value])

    const windowWidth = useWindowWidth()

    return (
      <VendorDatePicker
        minDate={minDate}
        onCalendarClose={() => onChange(selectedRange)}
        onChange={setSelectedRange}
        startDate={selectedRange[0] ?? undefined}
        endDate={selectedRange[1] ?? undefined}
        // TODO: The locale should be using userLocale, but it won't work
        // until we import the intended locale from date-fns.
        // At the moment we're only importing enGB. Dynamic import doesn't
        // seem to be working here.
        // Potential solution: `import * as locales from 'date-fns/locales`
        dateFormat="P"
        selectsRange
        popperPlacement="bottom"
        showPopperArrow={false}
        popperModifiers={[
          shift({
            padding: 10,
          }),
        ]}
        monthsShown={windowWidth > 768 ? 2 : 1}
        customInput={
          valueComponent ? (
            valueComponent
          ) : (
            <CustomInput
              clearButton={clearButton}
              onChange={() => onChange(selectedRange)}
              onClear={() => setSelectedRange([null, null])}
              selectedRange={selectedRange}
            />
          )
        }
      />
    )
  }
)

interface CustomInputProps extends Pick<DateRangePickerProps, 'clearButton' | 'onChange'> {
  onClear: MouseEventHandler
  onClick?: MouseEventHandler
  selectedRange: DateRangeValueFormat
  value?: string
}

const CustomInput = forwardRef<unknown, CustomInputProps>(
  ({ clearButton, onChange, onClear, onClick, selectedRange, value }, _ref) => {
    const { t } = useTranslation()

    return (
      <div className="relative">
        <Input
          label={t('date_range_picker.input.label')}
          name="dateRange"
          hideLabel
          value={value ?? ''}
          onChange={onChange}
          onFocus={onClick}
          className="sm:min-w-[190px]"
        />

        {clearButton && selectedRange.filter(Boolean).length > 0 && (
          <button
            type="button"
            onClick={onClear}
            title={t('date_range_picker.button.label')}
            className="absolute right-0 top-0 p-1 rounded-full m-1 text-gray-400 hover:text-violet"
          >
            <XIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    )
  }
)

interface FormikProps extends Pick<DateRangePickerProps, 'onChange'> {
  name: string
  value?: DateRangeValueFormat
}

export const FormikDateRangePicker = forwardRef<HTMLInputElement, FormikProps>(
  ({ name, value, ...props }, ref) => {
    return <DateRangePicker value={value} {...props} ref={ref} />
  }
)
